/**
 * @file TopNav.jsx
 * @description Top Navigation Bar
 *
 * @version 1.0.0
 * @author Carmelino Galang
 * @copyright Copyright (c) 2024, NetCyberOps LLC
 */

import React, {useContext, useEffect, useState, useRef, useCallback} from "react";
import {
    Dropdown,
    Image,
    OverlayTrigger,
    Toast,
    Tooltip,
} from "react-bootstrap";
import {Link} from "react-router-dom";
import {LogOut, MessageSquareText, Moon, Sun, UserRound} from "lucide-react";

import "./TopNav.css";
import SearchInput from "@components/SearchInput/SearchInput";
import UserFeedback from "@sections/Main/TopNav/modals/UserFeedback.jsx";
import ScopeSelector from "./ScopeSelector.jsx";

import {KeycloakContext} from "@context/KeycloakContext.jsx";
import {useSidebar} from "@context/SidebarContext.jsx";
import {useTheme, THEMES} from "@context/ThemeContext.jsx";
import {useWebSocket} from "@context/WebSocketContext.jsx";
import {useScope} from "@context/ScopeContext.jsx";
import {PROFILE_PIC_UPDATED_EVENT} from "@utils/profilePic";
// import Clock from "../Clock/Clock";

import Gear from "@assets/icons/gear.svg?react";
import Sorting from "@assets/icons/sorting.svg?react";

export default function TopNav({setSigningOut}) {
    const {connected} = useWebSocket();
    const {user, userType, logout, updateTokenAndFetch, profilePicURL, setProfilePicURL, getUserInfo} = useContext(KeycloakContext);
    const {isExpanded, setIsExpanded, setDisplayToolTipIcon} = useSidebar();
    const {theme, setTheme, toggleTheme} = useTheme();
    const {selectedScope, setSelectedScope, DEMO_SCOPE, forceDemoScope, setScopes, scopes, scopesLoading,
        setScopesLoading, hideScope, scopesRefreshKey} = useScope();

    const toggleMenu = () => {
        setIsExpanded(!isExpanded);
        setDisplayToolTipIcon(isExpanded);
    };

    const dropdownRef = useRef(null);

    const [userFeedbackModal, setUserFeedbackModal] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [profilePicLoading, setProfilePicLoading] = useState(true);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [profilePicId, setProfilePicId] = useState(null);
    const [profilePicVersion, setProfilePicVersion] = useState(0);

    // Sync theme from API only when localStorage has no saved value (e.g. new browser)
    useEffect(() => {
        const stored = localStorage.getItem("dashboard-theme");
        if (!stored && user?.theme && (user.theme === THEMES.DARK || user.theme === THEMES.LIGHT)) {
            setTheme(user.theme);
        }
    }, [user?.theme]);

    const handleToggleTheme = useCallback(async () => {
        const newTheme = theme === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK;
        toggleTheme();
        if (!user) return;
        try {
            await updateTokenAndFetch("/users/me", {
                method: "PUT",
                headers: {"Content-Type": "application/x-www-form-urlencoded"},
                body: new URLSearchParams({
                    first_name: user.first_name ?? "",
                    last_name: user.last_name ?? "",
                    display_name: user.display_name ?? "",
                    timezone: user.timezone ?? "UTC",
                    time_format: user.time_format ?? "12h",
                    theme: newTheme,
                }),
            });
        } catch (error) {
            console.error("Failed to save theme preference:", error);
        }
    }, [theme, toggleTheme, updateTokenAndFetch, user]);

    // Update profilePicId when user changes
    useEffect(() => {
        if (!user?.['profile_pic_id']) {
            setProfilePicURL(null);
            setProfilePicId(null);
        } else if (user?.['profile_pic_id'] !== profilePicId) {
            setProfilePicId(user?.['profile_pic_id']);
        }
    }, [user]);

    const getProfilePicForNav = useCallback(async (fileId, bypassCache) => {
        try {
            const response = await updateTokenAndFetch(`/files/${fileId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                cache: bypassCache ? "reload" : "force-cache"
            });
            if (response.ok) {
                // Convert response to blob
                const blob = await response.blob();
                const imageUrl = URL.createObjectURL(blob);
                setProfilePicURL(imageUrl);
                return imageUrl;
            }
        } catch (error) {
            console.error('Error fetching profile picture:', error);
        }
    }, [updateTokenAndFetch, setProfilePicURL]);

    useEffect(() => {
        let objectUrl = null;

        async function fetchProfilePic() {
            if (!profilePicId) {
                setProfilePicLoading(false);
                return;
            }

            setProfilePicLoading(true);

            try {
                objectUrl = await getProfilePicForNav(profilePicId, profilePicVersion > 0);
            } catch (error) {
                console.error('Profile picture fetch error:', error);
                setToastMessage(`❌ Error fetching your profile picture. Please contact your NCO representative.`);
                setShowToast(true);
            } finally {
                setProfilePicLoading(false);
            }
        }

        fetchProfilePic();
        // Cleanup function
        return () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [getProfilePicForNav, profilePicId, profilePicVersion]);

    useEffect(() => {
        const handleProfilePicUpdate = async () => {
            await getUserInfo?.();
            setProfilePicVersion((prev) => prev + 1);
        };
        window.addEventListener(PROFILE_PIC_UPDATED_EVENT, handleProfilePicUpdate);
        return () => window.removeEventListener(PROFILE_PIC_UPDATED_EVENT, handleProfilePicUpdate);
    }, [getUserInfo]);

    const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setShowDropdown(false);
        }
    };

    useEffect(() => {
        if (showDropdown) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showDropdown]);

    const handleLogout = (event) => {
        event.preventDefault();
        setSigningOut(true);
        logout();
    };

    return (
        <div id="top-nav-wrapper">
            <div id="top-nav-background"></div>
            <nav id="top-nav" className="d-flex justify-content-between">
                <Toast
                    className="toast"
                    show={showToast}
                    onClose={() => setShowToast(false)}
                    delay={5000}
                    autohide
                >
                    <Toast.Body>{toastMessage}</Toast.Body>
                </Toast>
                <div
                    id="top-nav-left-col"
                    className="d-flex justify-content-center align-items-center cursor-pointer gap-24px"
                >
                    <Sorting
                        onClick={toggleMenu}
                        className={`${
                            isExpanded ? "collapse-icon-active" : ""
                        } cursor-pointer collapse-icon `}
                    />
                    <SearchInput/>
                </div>
                {userType !== "customer" && !hideScope && (
                    <ScopeSelector
                        updateTokenAndFetch={updateTokenAndFetch}
                        selectedScope={selectedScope}
                        setSelectedScope={setSelectedScope}
                        demo_scope={DEMO_SCOPE}
                        forceDemoScope={forceDemoScope}
                        setScopes={setScopes}
                        scopes={scopes}
                        scopesLoading={scopesLoading}
                        setScopesLoading={setScopesLoading}
                        refreshKey={scopesRefreshKey}
                    />
                )}
                <div className="contact-details-container">
                    <div className="user-details w-100">
                        <div>
                            <OverlayTrigger
                                placement="bottom"
                                overlay={
                                    <Tooltip id="tooltip-bottom">
                                        {connected
                                            ? "Connected to Web Socket"
                                            : "Disconnected from the Web Socket"}
                                    </Tooltip>
                                }
                            >
                                <div
                                    id={"status-circle"}
                                    className={connected ? "connected" : ""}
                                ></div>
                            </OverlayTrigger>
                        </div>
                        <div className="user-name-container">
                            {user && user.display_name && (
                                <>
                                    <div className="name">
                                        {user.display_name}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="user-menu" ref={dropdownRef}>
                            <Dropdown align="end" show={showDropdown}>
                                <Dropdown.Toggle
                                    as="div"
                                    id={
                                        !profilePicLoading && profilePicURL !== null
                                            ? `user-dropdown-wrapper`
                                            : `default-user-dropdown-wrapper`
                                    }
                                    className="cursor-pointer"
                                    size="sm"
                                    onClick={() => {
                                        setShowDropdown((prev) => !prev);
                                    }}
                                >
                                    {(profilePicLoading || profilePicURL === false) ? null : profilePicURL ? (
                                        <div id="profile-pic-container">
                                            <Image src={profilePicURL} id="profile-pic"/>
                                        </div>
                                    ) : (
                                        <div id="user-dropdown">
                                            <UserRound size={28}/>
                                        </div>
                                    )}
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    <Link
                                        to="/settings/account"
                                        className="dropdown-item top-nav-btn"
                                        onClick={() => setShowDropdown(false)}
                                    >
                                        <Gear id="top-nav-settings-icon"/>
                                        Settings
                                    </Link>
                                    {/* <Dropdown.Item
                                        as="button"
                                        onClick={handleToggleTheme}
                                        className="top-nav-btn dropdown-item"
                                        id="top-nav-theme-btn"
                                    >
                                        {theme === THEMES.DARK
                                            ? <Sun id="top-nav-settings-icon"/>
                                            : <Moon id="top-nav-settings-icon"/>
                                        }
                                        {theme === THEMES.DARK ? "Light Mode" : "Dark Mode"}
                                    </Dropdown.Item> */}
                                    <Dropdown.Item
                                        as="button"
                                        onClick={() => {
                                            setUserFeedbackModal(true);
                                            setShowDropdown(false);
                                        }}
                                        className="top-nav-btn dropdown-item"
                                        id="top-nav-feedback-btn"
                                    >
                                        <MessageSquareText id="feedback-icon"/>
                                        Give us feedback
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        as="button"
                                        onClick={handleLogout}
                                        className="top-nav-btn dropdown-item"
                                        id="top-nav-logout-btn"
                                    >
                                        <LogOut id="top-nav-settings-icon"/>
                                        Logout
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                </div>
                <UserFeedback
                    modalShow={userFeedbackModal}
                    setModalShow={setUserFeedbackModal}
                    setShowToast={setShowToast}
                    setToastMessage={setToastMessage}
                />
            </nav>
        </div>
    );
}